import {
  Link,
  Text,
} from '@chakra-ui/react';
import Chart from 'react-apexcharts';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import {
  useDQAssetManagement,
} from '@src/modules/performance/context/reports-api-hooks';
import { useRecoilValue } from 'recoil';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Annual trend for results at the expected standard',
  content: [
    {
      title: 'At expected standard',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      Every metric in the Data Quality Framework has a threshold that defines when the RCA’s data has met an acceptable level of quality i.e. is at the expected standard. Each metric also has thresholds indicating where there are Minor issues with the data and where there are Major issues.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The RCA&apos;s annual results (%) are based on the number of asset management data quality metrics achieving the expected standard.
    </Text>
    <Text fontSize="inherit" mb={2}>
      As for the score, previous years&apos; results are recalculated using the metric criteria for the most recent financial year. This allows for a more even comparison against historical results.
    </Text>
    <Text fontSize="inherit" mb={2}>
      Further reference:
      <br />
      <Link href="https://www.nzta.govt.nz/assets/Road-Efficiency-Group/docs/practice-overviews/REG-practice-overview-data-quality-framework.pdf" isExternal>Data quality framework overview</Link>
      <br />
      <Link href="https://www.nzta.govt.nz/assets/Road-Efficiency-Group/docs/practice-overviews/REG-practice-overview-data-quality-dimensions.pdf" isExternal>Data quality dimensions overview</Link>
      <br />
      <Link href="https://www.nzta.govt.nz/assets/Road-Efficiency-Group/docs/RCA-data-quality-reports/asset-management-data-quality-report-score-calculation.pdf" isExternal>Overall Asset Management Score calculation</Link>
    </Text>
  </>,
      source: <Text fontSize="inherit" as="span">Road Efficiency Group Te Ringa Maimoa annual data quality assessment and reporting undertaken within Transport Insights.</Text>,
    },
  ],
};

function DataQualityExpectedStandard() {
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const { data, isLoading } = useDQAssetManagement();
  const distinctYears = [...new Set(data?.map((item) => item.Financial_Year))];
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }
  const lockYear = data?.[0]?.LockYear || null;

  // Filter out the National data when National is selected
  const chartData = [
    {
      name: currentDatabase?.name,
      type: 'line',
      data: data?.map((item) => ({ x: item.Financial_Year, y: item.AM })),
      color: CHART_COLOURS.blue,
    },
    ...(!isNational
      ? [
        {
          name: 'National',
          type: 'line',
          data: data?.map((item) => ({ x: item.Financial_Year, y: item.National_AM })),
          color: CHART_COLOURS.grey,
        },
      ]
      : []),
    {
      name: 'Peer Group',
      type: 'line',
      data: data?.map((item) => ({ x: item.Financial_Year, y: item.Peer_AM })),
      color: CHART_COLOURS.black,
    },
  ];

  // Filter out the Peer Group data
  const filteredChartData = filterChartDataByName(
    chartData,
    chartFilters,
  );

  // Extract all values from the chart data for min/max calculations
  const allValues = [...new Set(
    filteredChartData?.flatMap((x) => x.data?.flatMap((y) => (Array.isArray(y.y) ? y.y : [y.y]))),
  )];

  // Set min and max values for y-axis
  const minY = Math.min(...allValues);
  const maxY = Math.max(...allValues);

  return (
    <ReportSection
      title="Annual trend for results at the expected standard"
      informationSource={informationSource}
      isLoading={isLoading || !data}
      lockYear={lockYear}
    >
      <Chart
        options={
          {
            title: {
              text: 'At expected standard',
            },
            chart: {
              animations: {
                enabled: false,
              },
            },
            xaxis: {
              categories: distinctYears,
              labels: {
                offsetY: 5,
                rotate: -45,
                rotateAlways: true,
              },
            },
            yaxis: [
              {
                forceNiceScale: true,
                min: minY,
                max: maxY,
                labels: {
                  formatter(y) {
                    return `${y.toFixed(1)}%`;
                  },
                },
              },
            ],
            grid: {
              padding: {
                left: 20,
              },
            },
            dataLabels: {
              enabled: false,
            },
            stroke: {
              curve: 'straight',
              width: distinctYears.length > 1 ? 2 : 0,
            },
            tooltip: {
              shared: true,
              intersect: false,
              y: {
                formatter(value) {
                  return `${value.toFixed(1)}%`;
                },
              },
            },
          }
        }
        series={filteredChartData}
        type="line"
        width="280"
        height="280"
      />
      <Text
        align="center"
        mt={4}
        fontSize="xs"
        className="report-source-text"
      >
        Source: Road Efficiency Group
        <br />
        Te Ringa Maimoa Transport Insights
      </Text>
    </ReportSection>
  );
}

export default DataQualityExpectedStandard;
